import React, { useState } from "react";
import { Download, Upload, FileText, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useContractPermissions } from "@/hooks/use-contract-permissions";
import { getAuthHeaders } from "@/lib/auth-utils";

const ContractBulkActions = ({ selectedEmployees = [], onActionComplete }) => {
  const { toast } = useToast();
  const { canManageContract } = useContractPermissions();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBulkDownload = async () => {
    if (!selectedEmployees.length) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner au moins un employé",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const contractPromises = selectedEmployees.map(async (employee) => {
        try {
          const response = await fetch(
            `/api/v1/employees/${employee.id}/contract`,
            {
              headers: getAuthHeaders(),
            }
          );

          if (response.ok) {
            const blob = await response.blob();
            if (blob.size > 0) {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.style.display = "none";
              a.href = url;
              a.download = `contrat_${employee.firstName}_${employee.lastName}.pdf`;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
              return { success: true, employee };
            }
          }
          return { success: false, employee };
        } catch (error) {
          return { success: false, employee, error };
        }
      });

      const results = await Promise.all(contractPromises);
      const successful = results.filter((r) => r.success);
      const failed = results.filter((r) => !r.success);

      if (successful.length > 0) {
        toast({
          title: "Téléchargement réussi",
          description: `${successful.length} contrat(s) téléchargé(s)`,
        });
      }

      if (failed.length > 0) {
        toast({
          title: "Certains téléchargements ont échoué",
          description: `${failed.length} contrat(s) n'ont pas pu être téléchargés`,
          variant: "destructive",
        });
      }

      onActionComplete?.();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors du téléchargement",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const generateContractReport = async () => {
    if (!selectedEmployees.length) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner au moins un employé",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const contractStatuses = await Promise.all(
        selectedEmployees.map(async (employee) => {
          try {
            const response = await fetch(
              `/api/v1/employees/${employee.id}/contract`,
              {
                headers: getAuthHeaders(),
              }
            );

            const hasContract = response.ok && (await response.blob()).size > 0;

            return {
              id: employee.id,
              firstName: employee.firstName,
              lastName: employee.lastName,
              department: employee.department,
              position: employee.position,
              hasContract: hasContract,
            };
          } catch {
            return {
              id: employee.id,
              firstName: employee.firstName,
              lastName: employee.lastName,
              department: employee.department,
              position: employee.position,
              hasContract: false,
            };
          }
        })
      );

      // Créer un rapport CSV
      const csvContent = [
        ["Prénom", "Nom", "Département", "Poste", "Statut Contrat"],
        ...contractStatuses.map((emp) => [
          emp.firstName,
          emp.lastName,
          emp.department || "",
          emp.position || "",
          emp.hasContract ? "Présent" : "Manquant",
        ]),
      ]
        .map((row) => row.join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `rapport_contrats_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      link.click();

      toast({
        title: "Rapport généré",
        description: "Le rapport des contrats a été téléchargé",
      });

      onActionComplete?.();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la génération du rapport",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (!canManageContract()) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {selectedEmployees.length > 0 && (
        <Badge variant="secondary" className="text-xs">
          <Users className="w-3 h-3 mr-1" />
          {selectedEmployees.length} sélectionné(s)
        </Badge>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={isProcessing}>
            <FileText className="w-4 h-4 mr-2" />
            Actions Contrats
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions en lot</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleBulkDownload}
            disabled={!selectedEmployees.length || isProcessing}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger les contrats
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={generateContractReport}
            disabled={!selectedEmployees.length || isProcessing}
          >
            <FileText className="w-4 h-4 mr-2" />
            Générer rapport
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ContractBulkActions;
